import React from 'react';
import { CommonUtils, ProjectDetail } from './commonUtils';

// 공통 타입 정의
export interface FormData {
  brandCode: string;
  categoryCode: string;
  itemCode: string;
  status: string;
  name: string;
  shortName: string;
  posCode: string;
  originalPrice: number;
  salePrice: number;
  comment: string;
  rewardStampCount: number;
  rewardPointRatio: number;
  discount: number;
  voucher: number;
  reservation: number;
  delivery: number;
  shipping: number;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface FormErrors {
  brandCode?: string;
  categoryCode?: string;
  itemCode?: string;
  status?: string;
  name?: string;
  originalPrice?: string;
  salePrice?: string;
  rewardStampCount?: string;
  rewardPointRatio?: string;
  discount?: string;
  voucher?: string;
  reservation?: string;
  delivery?: string;
  shipping?: string;
}

// 공통 유틸리티 함수들
export class ProductFormUtils {
  // 폼 검증 함수 (기본)
  static validateForm(formData: FormData): FormErrors {
    return this.validateCreateForm(formData);
  }

  // 상품 코드 허용 문자: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)
  static readonly CODE_PATTERN = /^[a-zA-Z0-9_-]*$/;

  // 상품 생성 폼 검증
  static validateCreateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.itemCode) newErrors.itemCode = '상품 코드는 필수입니다';
    else if (!ProductFormUtils.CODE_PATTERN.test(formData.itemCode)) {
      newErrors.itemCode = '상품 코드는 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    }
    if (!formData.name) newErrors.name = '상품명은 필수입니다';
    if (formData.originalPrice === null || formData.originalPrice === undefined || Number.isNaN(formData.originalPrice)) {
      newErrors.originalPrice = '정가는 필수입니다';
    }
    if (formData.salePrice === null || formData.salePrice === undefined || Number.isNaN(formData.salePrice)) {
      newErrors.salePrice = '판매가는 필수입니다';
    }
    
    // Format validations
    if (formData.originalPrice < 0) {
      newErrors.originalPrice = '정가는 0 이상이어야 합니다';
    }
    if (formData.salePrice < 0) {
      newErrors.salePrice = '판매가는 0 이상이어야 합니다';
    }
    const isBothPriceZeroOnCreate = formData.originalPrice === 0 && formData.salePrice === 0;
    if (!isBothPriceZeroOnCreate && formData.salePrice > formData.originalPrice) {
      newErrors.salePrice = '판매가는 정가보다 클 수 없습니다';
    }
    if (formData.rewardStampCount < 0) {
      newErrors.rewardStampCount = '리워드 스탬프 수는 0 이상이어야 합니다';
    }
    if (formData.rewardPointRatio < 0 || formData.rewardPointRatio > 1) {
      newErrors.rewardPointRatio = '포인트 적립률은 0과 1 사이의 값이어야 합니다';
    }
    
    return newErrors;
  }

  // 상품 수정 폼 검증
  static validateUpdateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields (수정 시에는 일부 필드가 선택적일 수 있음)
    if (!formData.itemCode) newErrors.itemCode = '상품 코드는 필수입니다';
    else if (!ProductFormUtils.CODE_PATTERN.test(formData.itemCode)) {
      newErrors.itemCode = '상품 코드는 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 사용할 수 있습니다.';
    }
    if (!formData.name) newErrors.name = '상품명은 필수입니다';
    if (formData.originalPrice < 0) {
      newErrors.originalPrice = '정가는 0 이상이어야 합니다';
    }
    if (formData.salePrice < 0) {
      newErrors.salePrice = '판매가는 0 이상이어야 합니다';
    }
    const isBothPriceZeroOnUpdate = formData.originalPrice === 0 && formData.salePrice === 0;
    if (!isBothPriceZeroOnUpdate && formData.salePrice > formData.originalPrice) {
      newErrors.salePrice = '판매가는 정가보다 클 수 없습니다';
    }
    if (formData.rewardStampCount < 0) {
      newErrors.rewardStampCount = '리워드 스탬프 수는 0 이상이어야 합니다';
    }
    if (formData.rewardPointRatio < 0 || formData.rewardPointRatio > 1) {
      newErrors.rewardPointRatio = '포인트 적립률은 0과 1 사이의 값이어야 합니다';
    }
    
    return newErrors;
  }

  // 체크박스 핸들러
  static handleCheckboxChange(
    name: keyof FormData,
    checked: boolean,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
    
    // 에러 메시지 제거
    setErrors(prev => ({ ...prev, [name]: '' }));
  }

  // 입력값 변경 처리 함수
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked ? 1 : 0
      }));
    } else {
      // 상품 코드: 영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 허용
      const nextValue = name === 'itemCode'
        ? String(value).replace(/[^a-zA-Z0-9_-]/g, '')
        : name.includes('Price') || name.includes('Count') || name.includes('Ratio') || name.includes('discount') || name.includes('voucher') || name.includes('reservation') || name.includes('delivery') || name.includes('shipping')
          ? parseFloat(value) || 0
          : value;
      setFormData(prev => ({
        ...prev,
        [name]: nextValue
      }));
    }
    
    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  }

  // 상품 상태 뱃지 생성
  static getStatusBadge(status: string) {
    return CommonUtils.getStatusBadge(status, 'product');
  }

  // 뱃지 아이콘 및 색상 생성
  static getBadgeIcon(badge: string) {
    return CommonUtils.getBadgeIcon(badge);
  }

  static getBadgeColor(badge: string) {
    return CommonUtils.getBadgeColor(badge);
  }

  // 날짜 포맷팅
  static formatDate(dateString: string): string {
    return CommonUtils.formatDate(dateString);
  }

  // 가격 포맷팅
  static formatPrice(price: number): string {
    return CommonUtils.formatPrice(price);
  }

  // 프로젝트 정보 로드
  static loadProjectDetail(): any {
    return CommonUtils.loadProjectDetail();
  }

  // 기본 폼 데이터
  static getDefaultFormData(): FormData {
    return {
      brandCode: '',
      categoryCode: '',
      itemCode: '',
      status: 'ACTIVE',
      name: '',
      shortName: '',
      posCode: '',
      originalPrice: 0,
      salePrice: 0,
      comment: '',
      rewardStampCount: 0,
      rewardPointRatio: 0,
      discount: 0,
      voucher: 0,
      reservation: 0,
      delivery: 0,
      shipping: 0,
      description: ''
    };
  }

  // 모바일 사이드바 리사이즈 핸들러
  static useMobileSidebarResize(
    setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    return CommonUtils.useMobileSidebarResize(setIsMobileSidebarOpen);
  }

  // 체크박스 핸들러
  static handleSelectAll(
    selectedProducts: string[],
    filteredProducts: any[],
    setSelectedProducts: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectAll(selectedProducts, filteredProducts, setSelectedProducts, (product) => product.code);
  }

  static handleSelectProduct(
    productCode: string,
    selectedProducts: string[],
    setSelectedProducts: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectItem(productCode, selectedProducts, setSelectedProducts);
  }

  // 페이지네이션 핸들러
  static handlePageChange(
    page: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageChange(page, setCurrentPage);
  }

  static handlePageSizeChange(
    size: number,
    setPageSize: React.Dispatch<React.SetStateAction<number>>,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  }

  // 정렬 핸들러
  static handleSort(
    field: string,
    currentSortField: string,
    currentSortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSort(field, currentSortField, currentSortOrder, setSortField, setSortOrder);
  }

  // 검색 및 필터 핸들러
  static handleSearch(
    searchTerm: string,
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSearch(searchTerm, setSearchTerm);
  }

  static handleStatusFilter(
    status: string,
    setSelectedStatus: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleStatusFilter(status, setSelectedStatus);
  }

  // 뷰 타입 변경 핸들러
  static handleViewTypeChange(
    newViewType: string,
    setViewType: React.Dispatch<React.SetStateAction<string>>,
    location: any,
    navigate: any
  ) {
    return CommonUtils.handleViewTypeChange(newViewType, setViewType, location, navigate, '/products');
  }

  // 상품 생성 페이지로 이동
  static handleProductCreate(navigate: (path: string) => void) {
    navigate('/products/create');
  }

  // 상품 수정 페이지로 이동
  static handleProductEdit(navigate: (path: string) => void, productCode: string, currentPage: number, pageSize: number) {
    navigate(`/products/${encodeURIComponent(productCode)}/update?page=${currentPage}&size=${pageSize}`);
  }

  // 상품 삭제 처리
  static handleProductDelete(productCode: string, onDelete?: (code: string) => void) {
    return CommonUtils.handleDelete(productCode, '상품', onDelete);
  }

  // 폼 제출 처리 (상품 생성)
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getProjectCode: () => string,
    createProduct: (projectCode: string, data: any) => Promise<any>,
    navigate: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const productData = {
        brandCode: formData.brandCode || undefined,
        categoryCode: formData.categoryCode || undefined,
        itemCode: formData.itemCode,
        status: formData.status,
        name: formData.name,
        shortName: formData.shortName || undefined,
        posCode: formData.posCode || undefined,
        originalPrice: formData.originalPrice,
        salePrice: formData.salePrice,
        comment: formData.comment || undefined,
        rewardStampCount: formData.rewardStampCount,
        rewardPointRatio: formData.rewardPointRatio,
        discount: formData.discount,
        voucher: formData.voucher,
        reservation: formData.reservation,
        delivery: formData.delivery,
        shipping: formData.shipping,
        description: formData.description || undefined
      };

      const result = await createProduct(projectCode, productData);
      
      if (result.success) {
        alert('상품이 성공적으로 생성되었습니다.');
        navigate('/products');
      } else {
        setSubmitError(result.message || '상품 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('상품 생성 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '상품 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 폼 제출 처리 (상품 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getProjectCode: () => string,
    productCode: string,
    updateProduct: (projectCode: string, productCode: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const productData = {
        brandCode: formData.brandCode || undefined,
        categoryCode: formData.categoryCode || undefined,
        itemCode: formData.itemCode,
        status: formData.status,
        name: formData.name,
        shortName: formData.shortName || undefined,
        posCode: formData.posCode || undefined,
        originalPrice: formData.originalPrice,
        salePrice: formData.salePrice,
        comment: formData.comment || undefined,
        rewardStampCount: formData.rewardStampCount,
        rewardPointRatio: formData.rewardPointRatio,
        discount: formData.discount,
        voucher: formData.voucher,
        reservation: formData.reservation,
        delivery: formData.delivery,
        shipping: formData.shipping,
        description: formData.description || undefined
      };

      const result = await updateProduct(projectCode, productCode, productData);
      
      if (result.success) {
        setHasChanges(false);
        setOriginalData({...formData});
        // 상품 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/products?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '상품 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('상품 수정 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '상품 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }
}